import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../services/project.service';
import { confirmMicroserviceToggle, deleteProjectDialog, errorDialog, showError, successDialog, toggleStatusDialog } from 'src/app/shared/dialog';
import { MicroserviceVM } from '../models/MicroserviceVM';
import { forkJoin } from 'rxjs';



@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.css']
})
export class ProjectFormComponent implements OnInit {

  mode: 'create' | 'view' | 'edit' = 'create';
  projectId: string = "";

  domain = "";
  description = "";
  envVariablesJson = "{}";
  active: boolean = true;
  creationDate: string = "";

  allMicroservices: MicroserviceVM[] = [];
  loadingMicroservices = false;
  enabledMicroserviceNames = new Set<string>();

  supportedLanguages: string = "en";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService
  ) { }

  ngOnInit(): void {

    const path = this.route.snapshot.routeConfig?.path;

    if (path === 'create-project') this.mode = 'create';
    if (path === 'project-details/:id') this.mode = 'view';
    if (path === 'project-edit/:id') this.mode = 'edit';

    this.projectId = this.route.snapshot.paramMap.get('id') || "";

    if (this.mode !== 'create') this.loadProject();

  }

  // -------------------------------------------------------
  // LOAD PROJECT DETAILS
  // -------------------------------------------------------
  loadProject() {
    this.projectService.getProjectById(this.projectId).subscribe({
      next: (p: any) => {
        this.projectId = p.projectId;
        this.domain = p.domain;
        this.description = p.description;
        this.envVariablesJson = p.envVariablesJson || "{}";
        this.active = p.active;
        this.creationDate = p.creationDate;
        this.supportedLanguages = p.supportedLanguages || "en";


        // 🔑 extract enabled microservices ONCE
        this.enabledMicroserviceNames = new Set(
          (p.microservices || '')
            .split(',')
            .map((s: string) => s.trim().toLowerCase())
            .filter((s: string) => s.length > 0)
        );



        if (this.mode === 'edit' || this.mode === 'view') {
          this.loadAllMicroservices();
        }


        if (this.mode === 'view') {
          setTimeout(() => {
            document.querySelectorAll("input, textarea, select")
              .forEach((el: any) => el.disabled = true);
          }, 50);
        }
      },
      error: err => console.error("Failed to load project", err)
    });
  }
  // -------------------------------------------------------
  // CREATE PROJECT
  // -------------------------------------------------------
  createProject() {
    const payload = {
      domain: this.domain,
      description: this.description,
      envVariablesJson: this.envVariablesJson,
      active: this.active,
      supportedLanguages: this.supportedLanguages
    };

    this.projectService.createProject(payload).subscribe(() => {
      this.router.navigate(['/projects']);
    });
  }

  // -------------------------------------------------------
  // UPDATE PROJECT
  // -------------------------------------------------------
  saveChanges() {
    const payload = {
      projectId: this.projectId,
      domain: this.domain,
      description: this.description,
      envVariablesJson: this.envVariablesJson,
      active: this.active,
      supportedLanguages: this.supportedLanguages
    };

    this.projectService.updateProject(payload).subscribe(() => {
      this.router.navigate(['/projects']);
    });
  }

  // -------------------------------------------------------
  // TOGGLE STATUS
  // -------------------------------------------------------
  onStatusToggle() {
    const newValue = this.active;

    toggleStatusDialog(newValue).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          projectId: this.projectId,
          active: newValue,
        };

        this.projectService.updateProject(payload).subscribe({
          next: () => {
            successDialog(
              newValue ? "Project activated successfully!" : "Project deactivated!"
            );
          },
          error: () => {
            errorDialog("Failed to update the project status.");
            this.active = !newValue;
          },
        });
      } else {
        this.active = !newValue;
      }
    });
  }

  // -------------------------------------------------------
  // VIEW PAGE → GO TO EDIT
  // -------------------------------------------------------
  goToEdit() {
    this.router.navigate([`/project-edit/${this.projectId}`]);
  }

  // -------------------------------------------------------
  // DELETE PROJECT
  // -------------------------------------------------------
  deleteProject() {
    deleteProjectDialog().then((result: any) => {
      if (result.isConfirmed) {
        this.projectService.deleteProject(this.projectId).subscribe({
          next: () => {
            successDialog("Project deleted successfully!");
            this.router.navigate(['/projects']);
          },
          error: (err) => {
            const message =
              err?.error?.message ||
              err?.error?.details ||
              err?.message ||
              "An unexpected error occurred.";

            showError(message);
          },
        });
      }
    });
  }

  // -----------------------------------------------------
  // View Project
  // -----------------------------------------------------
  viewProject() {
    this.router.navigate(["/project-details", this.projectId]);
  }

  loadAllMicroservices() {
    this.loadingMicroservices = true;

    this.projectService.getAllMicroservices().subscribe({
      next: (all: any[]) => {

        if (this.mode === 'edit') {
          // EDIT → show all, enable some
          this.allMicroservices = all.map(m => ({
            serviceName: m.serviceName,
            enabled: this.enabledMicroserviceNames.has(
              m.serviceName.toLowerCase()
            )
          }));
        }

        if (this.mode === 'view') {
          // VIEW → show only enabled
          this.allMicroservices = all
            .filter(m =>
              this.enabledMicroserviceNames.has(m.serviceName.toLowerCase())
            )
            .map(m => ({
              serviceName: m.serviceName,
              enabled: true
            }));
        }

        this.loadingMicroservices = false;
      },
      error: () => this.loadingMicroservices = false
    });
  }



  onMicroserviceToggle(ms: MicroserviceVM, event: any) {
    const checked = event.target.checked;

    // Revert immediately until user confirms
    event.target.checked = !checked;

    confirmMicroserviceToggle(ms.serviceName, checked).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      if (checked) {
        this.projectService
          .addMicroservice(this.projectId, ms.serviceName)
          .subscribe({
            next: () => {
              ms.enabled = true;
              event.target.checked = true;
              successDialog(`"${ms.serviceName}" enabled successfully`);
            },
            error: () => {
              errorDialog("Failed to enable microservice");
            }
          });
      } else {
        this.projectService
          .removeMicroservice(this.projectId, ms.serviceName)
          .subscribe({
            next: () => {
              ms.enabled = false;
              event.target.checked = false;
              successDialog(`"${ms.serviceName}" disabled successfully`);
            },
            error: () => {
              errorDialog("Failed to disable microservice");
            }
          });
      }
    });
  }






}
